// Stimulus controller for the member's private note on a model book setup.
//
// The note block lives inside the chart header, and the chart header is
// rewritten in place when a row is clicked — so this controller has to do
// two things:
//
//   1. save() / destroy() — write the note without a page load, and say so.
//   2. load(payload)      — public: refill the form for a DIFFERENT setup.
//      vcp-annotations calls it with the `member_note` key of the chart-data
//      payload it just fetched, which is why that key is top level and not
//      buried in the member-agnostic overlay.
//
// Nothing here decides what a member may see: the server only ever builds
// `ModelBookNote.for(current_user, setup)`, so the worst a broken url value
// can do is write the member's own note against the wrong setup.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "form", "deleteForm", "body", "tagInput", "status", "deleteButton" ]
  static values = { updateUrl: String }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  async save(event) {
    event.preventDefault()

    const payload = await this.send("PUT", JSON.stringify({
      model_book_note: {
        body: this.hasBodyTarget ? this.bodyTarget.value : "",
        tag_input: this.hasTagInputTarget ? this.tagInputTarget.value : ""
      }
    }))
    if (!payload) return

    // Reflect the NORMALISED tags back: the member typed "Handle, handle",
    // and the stored vocabulary is what the filter and the badge will show.
    if (this.hasTagInputTarget) this.tagInputTarget.value = payload.tag_input || ""
    this.showDelete(Boolean(payload.updated_at))
    this.setStatus("Saved")
    this.announce(payload)
  }

  // The Delete button is a real submit of the DELETE form (the no-JS path);
  // with JS it becomes a fetch, so stop the native submit first.
  async destroy(event) {
    event?.preventDefault()

    const payload = await this.send("DELETE", null)
    if (!payload) return

    if (this.hasBodyTarget) this.bodyTarget.value = ""
    if (this.hasTagInputTarget) this.tagInputTarget.value = ""
    this.showDelete(false)
    this.setStatus("Deleted")
    this.announce(payload)
  }

  // Public: fill the form for whichever setup the chart now shows.
  //
  // A payload for the setup already in the box is IGNORED. The model book
  // renders this form server-side and then fetches the selected setup's
  // chart data on connect, so a blind refill would wipe whatever the member
  // had already started typing — which is exactly what it did the first time.
  load(payload) {
    if (!payload) return
    if (payload.update_url && payload.update_url === this.updateUrlValue) return

    if (this.hasBodyTarget) this.bodyTarget.value = payload.body || ""
    if (this.hasTagInputTarget) this.tagInputTarget.value = payload.tag_input || ""
    if (payload.update_url) {
      this.updateUrlValue = payload.update_url
      if (this.hasFormTarget) this.formTarget.action = payload.update_url
      if (this.hasDeleteFormTarget) this.deleteFormTarget.action = payload.update_url
    }
    this.showDelete(Boolean(payload.updated_at))
    this.setStatus("")
  }

  // --- helpers -------------------------------------------------------------

  // One request shape for both verbs. Returns the note payload, or null once
  // it has shown the refusal — the caller never has to check twice.
  async send(method, body) {
    if (!this.updateUrlValue) return null

    try {
      const response = await fetch(this.updateUrlValue, {
        method: method,
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: body
      })

      // An expired session answers with a 303 to the sign-in page, which
      // fetch follows: a 200 of HTML. Parsing it as JSON would throw into
      // the generic "Try again" below — and a retry can never succeed. Say
      // what would.
      if (response.redirected || !this.isJson(response)) {
        this.setStatus("Your session has expired — sign in again to save this note.")
        return null
      }
      const json = await response.json()

      if (!response.ok) {
        this.setStatus(json.error || "We couldn't save that note.")
        return null
      }
      return json.note
    } catch (err) {
      console.error("[model-book-note] Request failed", err)
      this.setStatus("We couldn't save that note. Try again.")
      return null
    }
  }

  // Tell the page's row list that this setup's note changed, so the "N" badge
  // can appear or go. vcp-annotations owns the rows; this controller owns the
  // form, and neither reaches into the other.
  announce(payload) {
    this.element.dispatchEvent(new CustomEvent("model-book-note:changed", {
      bubbles: true,
      detail: {
        // The route is /setups/setup/:id/note, and the id is the only
        // thing the row list needs. Carrying it in the payload instead would
        // mean a member-private endpoint echoing an id back to say which
        // setup it already knows it was asked about.
        setupId: Number(String(this.updateUrlValue).match(/setup\/(\d+)\/note/)?.[1]),
        noted: Boolean(payload.updated_at),
        tags: payload.tags || []
      }
    }))
  }

  isJson(response) {
    return (response.headers.get("Content-Type") || "").includes("application/json")
  }

  showDelete(visible) {
    if (this.hasDeleteButtonTarget) this.deleteButtonTarget.classList.toggle("d-none", !visible)
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}
